import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Box,
  Alert,
  CircularProgress,
  LinearProgress,
  IconButton,
  Tooltip,
  StepConnector,
  stepConnectorClasses
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Close as CloseIcon,
  FlightTakeoff as FlightTakeoffIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import BasicInfoForm from './YuyanProjectDialog/BasicInfoForm';
import TeamMembersForm from './YuyanProjectDialog/TeamMembersForm';
import DocumentUploadForm from './YuyanProjectDialog/DocumentUploadForm';
import { EDUGATE_API_URL } from '../config/serviceConfig';
import ProjectListView from './YuyanProjectDialog/ProjectListView';
import EvaluationView from './YuyanProjectDialog/EvaluationView';

// 自定义Stepper样式
const ColorlibConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'none',
      backgroundColor: 'var(--workbench-accent, #4D8BFF)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'none',
      backgroundColor: 'var(--workbench-accent, #4D8BFF)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 1,
  },
}));

const ColorlibStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: 'rgba(255,255,255,0.1)',
  zIndex: 1,
  color: '#fff',
  width: 40,
  height: 40,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  border: '1px solid rgba(255,255,255,0.2)',
  ...(ownerState.active && {
    backgroundImage: 'none',
    backgroundColor: 'var(--workbench-accent, #4D8BFF)',
    boxShadow: 'none',
    border: 'none',
  }),
  ...(ownerState.completed && {
    backgroundImage: 'none',
    backgroundColor: 'var(--workbench-accent, #4D8BFF)',
    border: 'none',
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className } = props;

  const icons = {
    1: <span>1</span>,
    2: <span>2</span>,
    3: <span>3</span>,
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active }} className={className}>
      {completed ? <CheckIcon fontSize="small" /> : icons[String(props.icon)]}
    </ColorlibStepIconRoot>
  );
}

const YuyanProjectDialog = ({ open, onClose, currentUser }) => {
  const { t } = useTranslation();
  // CAS用户获取student_id的工具函数
  const getCasUserStudentId = async (employeeNumber, userName, userType = 'bupt_cas', phone = '') => {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'X-Cas-User-ID': encodeURIComponent(employeeNumber),
        'X-User-Name': encodeURIComponent(userName),
        'X-User-Type': userType // ✅ 添加用户类型
      };
      
      const response = await fetch(`${EDUGATE_API_URL}/api/auth/get-student-id`, {
        method: 'POST',
        headers: headers
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.code === 0) {
          return result.data.student_id;
        }
      }
      return null;
    } catch (error) {
      console.error('获取CAS用户student_id失败:', error);
      return null;
    }
  };

  // 获取或缓存用户的student_id
  const getUserStudentId = async () => {
    console.log('getUserStudentId调用 - currentUser:', currentUser);
    
    // 如果已经有student_id，直接返回
    if (currentUser.student_id) {
      console.log('使用现有student_id:', currentUser.student_id);
      return currentUser.student_id;
    }
    
    // 如果是CAS用户且有employeeNumber，获取student_id
    if (currentUser.isCasUser && currentUser.employeeNumber) {
      console.log('CAS用户，正在获取student_id...', currentUser.employeeNumber, currentUser.name);
      const studentId = await getCasUserStudentId(
        currentUser.employeeNumber, 
        currentUser.name,
        currentUser.user_type || 'bupt_cas'
      );
      console.log('获取到的student_id:', studentId);
      if (studentId) {
        // 缓存到currentUser对象中，避免重复请求
        currentUser.student_id = studentId;
        console.log('student_id已缓存到currentUser');
        return studentId;
      }
    } else {
      console.log('非CAS用户或缺少employeeNumber:', {
        isCasUser: currentUser.isCasUser,
        employeeNumber: currentUser.employeeNumber
      });
    }
    
    // 其他情况返回空字符串
    console.log('返回空student_id');
    return '';
  };

  // 主要视图模式：list(项目列表)、create(新建项目)、edit(编辑项目)、upload(上传文档)、evaluation(查看评价)
  const [viewMode, setViewMode] = useState('list');
  
  // 文档上传类型：proposal(立项报告)、conclusion(结项报告)
  const [uploadDocumentType, setUploadDocumentType] = useState('proposal');
  
  // 步骤控制
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // 编辑状态
  const [editingProject, setEditingProject] = useState(null);
  const [uploadingProject, setUploadingProject] = useState(null);
  const [viewingEvaluationProject, setViewingEvaluationProject] = useState(null);
  
  // 表单数据
  const [basicInfo, setBasicInfo] = useState({
    projectName: '',
    projectCategory: '智能硬件',
    projectCategoryOther: '',
    projectNature: '硬件',
    projectNatureOther: '',
    college: '',
    description: '', // 项目描述，可选
    supervisorName: '',
    supervisorContact: ''
  });
  
  const [teamMembers, setTeamMembers] = useState([
    { 
      name: '', 
      college: '',
      major: '',
      className: '',
      studentId: '', 
      phone: '',
      introduction: '',
      isLeader: true 
    }
  ]);
  
  const [documents, setDocuments] = useState([]);
  const [userProjects, setUserProjects] = useState([]);
  
  // 步骤配置
  const getSteps = () => {
    if (viewMode === 'upload') {
      return [t('yuyan_step_upload')];
    }
    if (viewMode === 'create') {
      return [t('yuyan_step_basic'), t('yuyan_step_team')];
    }
    if (viewMode === 'edit') {
      return [t('yuyan_step_basic'), t('yuyan_step_team')];
    }
    return [t('yuyan_step_basic'), t('yuyan_step_team')];
  };

  // 重置表单
  const resetForm = () => {
    setActiveStep(0);
    setError('');
    setSuccess('');
    setLoadingMessage('');
    setEditingProject(null);
    setUploadingProject(null);
    setViewingEvaluationProject(null);
    setUploadDocumentType('');
    setBasicInfo({
      projectName: '',
      projectCategory: '智能硬件',
      projectCategoryOther: '',
      projectNature: '硬件',
      projectNatureOther: '',
      college: '',
      description: '', // 项目描述，可选
      supervisorName: '',
      supervisorContact: ''
    });
    setTeamMembers([{ 
      name: '', 
      schoolId: 1,
      schoolName: '北京邮电大学',
      college: '',
      major: '',
      className: '',
      studentId: '', 
      phone: '',
      introduction: '',
      isLeader: true 
    }]);
    setDocuments([]);
  };

  // 视图模式切换
  const handleViewModeChange = (newMode, type = 'proposal') => {
    setViewMode(newMode);
    setUploadDocumentType(type);
    resetForm();
  };

  // 获取用户项目列表
  const fetchUserProjects = async () => {
    const userId = currentUser.id || currentUser.studentNumber || currentUser.student_number || currentUser.username;
    
    // 获取student_id（对CAS用户自动获取）
    const studentId = await getUserStudentId();
    
    if (!studentId) {
      setError(t('yuyan_err_student_id'));
      return;
    }
    
    try {
      setIsLoading(true);
      setLoadingMessage(t('yuyan_loading_fetch'));
      const response = await fetch(`${EDUGATE_API_URL}/api/yuyan/projects?student_id=${studentId}`, {
        headers: {
          'Authorization': `Bearer ${currentUser.token || ''}`,
          'X-Student-ID': studentId.toString(),
          'X-User-ID': userId // 保留兼容性
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('API响应:', result);
        if (result.code === 0) {
          setUserProjects(result.data.projects || []);
          setLoadingMessage(t('yuyan_loading_done'));
        } else {
          setError(result.message || t('yuyan_err_fetch_list'));
        }
      } else {
        console.log('HTTP错误:', response.status, response.statusText);
        setError(t('yuyan_err_fetch_list_full', { status: response.status, statusText: response.statusText }));
      }
    } catch (error) {
      console.error('获取项目列表错误:', error);
      console.log('错误详情:', error.message);
      setError(t('yuyan_err_network_msg', { msg: error.message }));
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  // 项目管理功能
  const handleEditProject = (projectData) => {
    // 适应后端返回的嵌套数据结构
    const project = projectData.project || projectData;
    const teamMembers = projectData.team_members || projectData.teamMembers || [];
    
    setEditingProject(projectData);
    setViewMode('edit');
    // 根据项目状态确定申请类型
    if (project.status && project.status.includes('proposal')) {
      setUploadDocumentType('proposal');
    } else if (project.status && project.status.includes('conclusion')) {
      setUploadDocumentType('conclusion');
    } else {
      // 默认为立项申请
      setUploadDocumentType('proposal');
    }
    
    // 填充表单数据
    setBasicInfo({
      projectName: project.project_name || '',
      projectCategory: project.project_category || '智能硬件',
      projectCategoryOther: project.project_category_other || '',
      projectNature: project.project_nature || '硬件',
      projectNatureOther: project.project_nature_other || '',
      college: project.college || '',
      description: project.description || '',
      supervisorName: project.supervisor_name || '',
      supervisorContact: project.supervisor_contact || ''
    });
    
    // 转换团队成员数据格式，适应前端组件期望的字段名
    const convertedTeamMembers = teamMembers.map(member => ({
      name: member.name || '',
      schoolId: member.school_id || 1,
      schoolName: member.school_name || '北京邮电大学',
      college: member.college || '',
      major: member.major || '',
      className: member.class_name || '',
      studentId: member.student_id || '',
      phone: member.phone || '',
      introduction: member.introduction || '',
      isLeader: member.role === '项目负责人'
    }));
    setTeamMembers(convertedTeamMembers.length > 0 ? convertedTeamMembers : [{
      name: '', 
      schoolId: 1,
      schoolName: '北京邮电大学',
      college: '',
      major: '',
      className: '',
      studentId: '', 
      phone: '',
      introduction: '',
      isLeader: true 
    }]);
    
    // 设置现有文档数据
    const existingDocuments = projectData.documents || [];
    const convertedDocuments = existingDocuments.map(doc => ({
      id: doc.id || doc._id, // MongoDB ObjectID可能在id或_id字段
      name: doc.original_filename,
      size: doc.file_size || 0,
      type: doc.document_category,
      uploadTime: doc.created_at || doc.upload_time,
      isExisting: true, // 标记为现有文档
      isValid: true // 现有文档默认为有效
    }));
    setDocuments(convertedDocuments);
    
    setActiveStep(0);
  };

  const handleDeleteProject = async (projectData) => {
    // 适应后端返回的嵌套数据结构
    const project = projectData.project || projectData;
    const projectId = project.id;
    
    const userId = currentUser.id || currentUser.studentNumber || currentUser.student_number || currentUser.username;
    const studentId = await getUserStudentId();
    try {
      setIsLoading(true);
      setLoadingMessage(t('yuyan_loading_delete_project', { name: project.project_name }));
      const response = await fetch(`${EDUGATE_API_URL}/api/yuyan/projects/${projectId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${currentUser.token || ''}`,
          'X-Student-ID': studentId ? studentId.toString() : '',
          'X-User-ID': userId
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.code === 0) {
          setLoadingMessage(t('yuyan_loading_delete_done'));
          setSuccess(t('yuyan_success_delete'));
          fetchUserProjects(); // 刷新列表
        } else {
          setError(result.message || t('yuyan_err_delete'));
        }
      } else {
        setError(t('yuyan_err_delete'));
      }
    } catch (error) {
      console.error('删除项目错误:', error);
      setError(t('yuyan_err_network'));
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  const handleDocumentReupload = (projectData, documentType) => {
    setUploadingProject(projectData);
    setUploadDocumentType(documentType);
    setViewMode('upload');
    
    // 设置现有的对应类型文档
    const existingDocuments = projectData.documents || [];
    const relevantDocuments = existingDocuments
      .filter(doc => doc.document_category === documentType)
      .map(doc => ({
        id: doc.id || doc._id,
        name: doc.original_filename,
        size: doc.file_size || 0,
        type: doc.document_category,
        uploadTime: doc.upload_time || doc.created_at,
        isExisting: true,
        isValid: true
      }));
    
    setDocuments(relevantDocuments);
    setActiveStep(0);
  };

  const handleBackToList = () => {
    setViewMode('list');
    resetForm();
    fetchUserProjects();
  };

  // 处理查看评价
  const handleViewEvaluation = (projectData, evaluationType = '') => {
    const project = projectData.project || projectData;
    
    if (!project || !project.id) {
      console.error('项目数据无效:', projectData);
      setError(t('yuyan_err_project_invalid'));
      return;
    }
    
    // 将评价类型信息传递给评价组件
    const projectWithEvaluationType = {
      ...projectData,
      evaluationType: evaluationType
    };
    
    setViewingEvaluationProject(projectWithEvaluationType);
    setViewMode('evaluation');
  };

  // 表单验证
  const validateCurrentStep = () => {
    // 在上传文档模式下，验证是否有操作（上传新文档或删除现有文档）
    if (viewMode === 'upload') {
      const hasNewDocuments = documents.some(doc => !doc.isExisting && doc.file);
      const hasDeletedDocuments = documents.some(doc => doc.isExisting && doc.isDeleted);
      
      if (!hasNewDocuments && !hasDeletedDocuments) {
        setError(t('yuyan_err_select_doc'));
        return false;
      }
      setError('');
      return true;
    }

    switch (activeStep) {
      case 0: // 基本信息
        if (!basicInfo.projectName.trim()) {
          setError(t('yuyan_err_project_name'));
          return false;
        }
        if (!basicInfo.college.trim()) {
          setError(t('yuyan_err_fill_college'));
          return false;
        }
        break;
      case 1: // 团队成员
        const validMembers = teamMembers.filter(member => 
          member.name.trim() && member.college.trim() && member.major.trim() && 
          member.className.trim() && member.studentId.trim() && member.phone.trim()
        );
        if (validMembers.length === 0) {
          setError(t('yuyan_err_min_member'));
          return false;
        }
        const hasLeader = validMembers.some(member => member.isLeader);
        if (!hasLeader) {
          setError(t('yuyan_err_leader'));
          return false;
        }
        // 验证学号格式
        const invalidStudentId = validMembers.find(member => 
          !/^\d{4,12}$/.test(member.studentId)
        );
        if (invalidStudentId) {
          setError(t('yuyan_err_student_id_format'));
          return false;
        }
        // 验证电话格式
        const invalidPhone = validMembers.find(member => 
          !/^1[3-9]\d{9}$/.test(member.phone)
        );
        if (invalidPhone) {
          setError(t('yuyan_err_phone_format'));
          return false;
        }
        break;
      case 2: // 文档上传
        if (documents.length === 0) {
          setError(t('yuyan_err_upload_proposal'));
          return false;
        }
        break;
    }
    setError('');
    return true;
  };

  // 步骤导航
  const handleNext = () => {
    if (validateCurrentStep()) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError('');
  };

  // 提交申请
  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;
    
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      
      // 文档重新上传模式
      if (viewMode === 'upload') {
        setLoadingMessage(t('yuyan_loading_process'));
        const userId = currentUser.id || currentUser.studentNumber || currentUser.student_number || currentUser.username;
        const project = uploadingProject.project || uploadingProject;
        const projectId = project.id;
        
        if (!projectId) {
          setError(t('yuyan_err_project_id'));
          return;
        }
        
        // 处理删除的现有文档
        const deletedDocuments = documents.filter(doc => doc.isExisting && doc.isDeleted);
        if (deletedDocuments.length > 0) {
          setLoadingMessage(t('yuyan_loading_delete_docs', { count: deletedDocuments.length }));
        }
        for (const doc of deletedDocuments) {
          try {
            const deleteResponse = await fetch(`${EDUGATE_API_URL}/api/yuyan/documents/${doc.id}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${currentUser.token || ''}`,
                'X-User-ID': userId
              }
            });
            
            if (!deleteResponse.ok) {
              console.warn(`删除文档失败: ${doc.name}`);
            }
          } catch (error) {
            console.warn(`删除文档错误: ${doc.name}`, error);
          }
        }
        
        // 处理新上传的文档
        const newDocuments = documents.filter(doc => !doc.isExisting && doc.file);
        if (newDocuments.length > 0) {
          setLoadingMessage(t('yuyan_loading_upload_docs', { count: newDocuments.length }));
          for (let i = 0; i < newDocuments.length; i++) {
            const doc = newDocuments[i];
            setLoadingMessage(t('yuyan_loading_upload_one', { current: i + 1, total: newDocuments.length, name: doc.name }));
            
            const formData = new FormData();
            formData.append('project_id', projectId);
            formData.append('document_category', uploadDocumentType);
            formData.append('file', doc.file);
            // 不使用replace标记，让新文档和旧文档共存
            
            const uploadResponse = await fetch(`${EDUGATE_API_URL}/api/yuyan/projects/upload`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${currentUser.token || ''}`,
                'X-User-ID': userId
              },
              body: formData
            });
            
            if (!uploadResponse.ok) {
              const errorData = await uploadResponse.json();
              setError(t('yuyan_err_upload', { msg: errorData.message || t('yuyan_unknown_error') }));
              return;
            }
            
            const uploadResult = await uploadResponse.json();
            if (uploadResult.code !== 0) {
              setError(t('yuyan_err_upload', { msg: uploadResult.message || t('yuyan_unknown_error') }));
              return;
            }
          }
        }
        
        // 检查是否有操作（删除或上传）
        if (deletedDocuments.length === 0 && newDocuments.length === 0) {
          setError(t('yuyan_err_select_doc'));
          return;
        }
        
        setLoadingMessage(t('yuyan_loading_done_refresh'));
        setSuccess(t('yuyan_success_doc'));
        setTimeout(() => {
          handleBackToList();
        }, 2000);
        return;
      }
      
      // 编辑模式
      if (viewMode === 'edit') {
        setLoadingMessage(t('yuyan_loading_save'));
        const userId = currentUser.id || currentUser.studentNumber || currentUser.student_number || currentUser.username;
        const project = editingProject.project || editingProject;
        const projectId = project.id;
        
        if (!projectId) {
          setError(t('yuyan_err_project_id'));
          return;
        }
        
        const updateData = {
          project_name: basicInfo.projectName,
          project_category: basicInfo.projectCategory,
          college: basicInfo.college,
          description: basicInfo.description,
          supervisor_name: basicInfo.supervisorName,
          supervisor_contact: basicInfo.supervisorContact,
          team_members: teamMembers.filter(m => m.name.trim() && m.studentId.trim()).map(member => ({
            name: member.name,
            school_id: member.schoolId || member.school_id || 1,
            school_name: member.schoolName || member.school_name || '北京邮电大学',
            college: member.college,
            major: member.major,
            class_name: member.className,
            student_id: member.studentId,
            phone: member.phone,
            introduction: member.introduction,
            role: member.isLeader ? '项目负责人' : '团队成员'
          }))
        };
        
        const response = await fetch(`${EDUGATE_API_URL}/api/yuyan/projects/${projectId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentUser.token || ''}`,
            'X-User-ID': userId
          },
          body: JSON.stringify(updateData)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          setError(errorData.message || t('yuyan_err_update'));
          return;
        }
        
        const result = await response.json();
        if (result.code !== 0) {
          setError(result.message || t('yuyan_err_update'));
          return;
        }
        
        // 处理删除的现有文档
        const deletedDocuments = documents.filter(doc => doc.isExisting && doc.isDeleted);
        if (deletedDocuments.length > 0) {
          setLoadingMessage(t('yuyan_loading_delete_docs', { count: deletedDocuments.length }));
        }
        for (const doc of deletedDocuments) {
          try {
            const deleteResponse = await fetch(`${EDUGATE_API_URL}/api/yuyan/documents/${doc.id}`, {
              method: 'DELETE',
              headers: {
                'Authorization': `Bearer ${currentUser.token || ''}`,
                'X-User-ID': userId
              }
            });
            
            if (!deleteResponse.ok) {
              console.warn(`删除文档失败: ${doc.name}`);
            }
          } catch (error) {
            console.warn(`删除文档错误: ${doc.name}`, error);
          }
        }
        
        // 处理新上传的文档
        const newDocuments = documents.filter(doc => !doc.isExisting && doc.file);
        if (newDocuments.length > 0) {
          setLoadingMessage(t('yuyan_loading_upload_docs', { count: newDocuments.length }));
          for (let i = 0; i < newDocuments.length; i++) {
            const doc = newDocuments[i];
            setLoadingMessage(t('yuyan_loading_upload_one', { current: i + 1, total: newDocuments.length, name: doc.name }));
            
            const formData = new FormData();
            formData.append('project_id', projectId);
            formData.append('document_category', uploadDocumentType);
            formData.append('file', doc.file);
            
            const uploadResponse = await fetch(`${EDUGATE_API_URL}/api/yuyan/projects/upload`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${currentUser.token || ''}`,
                'X-User-ID': userId
              },
              body: formData
            });
            
            if (!uploadResponse.ok) {
              const errorData = await uploadResponse.json();
              setError(t('yuyan_err_upload', { msg: errorData.message || t('yuyan_unknown_error') }));
              return;
            }
            
            const uploadResult = await uploadResponse.json();
            if (uploadResult.code !== 0) {
              setError(t('yuyan_err_upload', { msg: uploadResult.message || t('yuyan_unknown_error') }));
              return;
            }
          }
        }
        
        setLoadingMessage(t('yuyan_success_save'));
        setSuccess(t('yuyan_success_update'));
        setTimeout(() => {
          handleBackToList();
        }, 2000);
        return;
      }
      
      // 新建项目模式
      setLoadingMessage(t('yuyan_loading_create'));
      const userId = currentUser.id || currentUser.studentNumber || currentUser.student_number || currentUser.username;
      
      // 获取student_id（对CAS用户自动获取）
      const studentId = await getUserStudentId();
      
      const projectData = {
        type: 'create', // 新建项目类型
        project_info: {
          project_name: basicInfo.projectName,
          project_category: basicInfo.projectCategory,
          project_category_other: basicInfo.projectCategoryOther,
          project_nature: basicInfo.projectNature,
          project_nature_other: basicInfo.projectNatureOther,
          college: basicInfo.college,
          description: basicInfo.description,
          applicant_name: currentUser.name || basicInfo.applicantName,
          applicant_id: userId,
          student_id: studentId, // 使用动态获取的student_id
          contact_info: basicInfo.contactInfo,
          supervisor_name: basicInfo.supervisorName,
          supervisor_contact: basicInfo.supervisorContact
        },
        team_members: teamMembers.filter(m => m.name.trim() && m.studentId.trim()).map(member => ({
          name: member.name,
          school_id: member.schoolId || member.school_id || 1,
          school_name: member.schoolName || member.school_name || '北京邮电大学',
          college: member.college,
          major: member.major,
          class_name: member.className,
          student_id: member.studentId,
          phone: member.phone,
          introduction: member.introduction,
          role: member.isLeader ? '项目负责人' : '团队成员'
        })),
        document_category: 'proposal' // 新建项目暂不上传文档
      };

      // 创建项目
      const response = await fetch(`${EDUGATE_API_URL}/api/yuyan/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentUser.token || ''}`,
          'X-Student-ID': studentId ? studentId.toString() : '',
          'X-User-ID': userId
        },
        body: JSON.stringify(projectData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.message || t('yuyan_err_create'));
        return;
      }

      const result = await response.json();
      if (result.code !== 0) {
        setError(result.message || t('yuyan_err_create'));
        return;
      }

      const projectId = result.data.project.id;
      
      setLoadingMessage(t('yuyan_success_create'));
      setSuccess(t('yuyan_success_create_msg', { id: projectId }));
      setTimeout(() => {
        handleBackToList();
      }, 3000);
    } catch (error) {
      console.error('提交错误:', error);
      setError(t('yuyan_err_network'));
    } finally {
      setIsLoading(false);
      setLoadingMessage('');
    }
  };

  // 关闭对话框
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // 初始化时获取项目列表
  useEffect(() => {
    if (open && viewMode === 'list') {
      fetchUserProjects();
    }
  }, [open, viewMode]);

  // 渲染步骤内容
  const renderStepContent = () => {
    if (viewMode === 'upload') {
      return (
        <DocumentUploadForm 
          data={documents} 
          onChange={setDocuments}
          mode={uploadDocumentType}
        />
      );
    }
    
    switch (activeStep) {
      case 0:
        return (
          <BasicInfoForm 
            data={basicInfo} 
            onChange={setBasicInfo}
            mode={uploadDocumentType}
            editMode={viewMode === 'edit'}
          />
        );
      case 1:
        return (
          <TeamMembersForm 
            data={teamMembers} 
            onChange={setTeamMembers}
            editMode={viewMode === 'edit'}
          />
        );
      case 2:
        return (
          <DocumentUploadForm 
            data={documents} 
            onChange={setDocuments}
            mode={uploadDocumentType}
          />
        );
      default:
        return null;
    }
  };

  // 渲染主要内容
  const renderMainContent = () => {
    // 项目列表视图
    if (viewMode === 'list') {
      return (
        <ProjectListView
          projects={userProjects}
          isLoading={isLoading}
          onRefresh={fetchUserProjects}
          onEdit={handleEditProject}
          onDelete={handleDeleteProject}
          onDocumentReupload={handleDocumentReupload}
          onViewEvaluation={handleViewEvaluation}
          currentUser={currentUser}
        />
      );
    }

    // 评价查看视图
    if (viewMode === 'evaluation') {
      return (
        <EvaluationView
          projectData={viewingEvaluationProject}
          currentUser={currentUser}
          onBack={handleBackToList}
        />
      );
    }
    
    // 表单视图（新建、编辑、重新上传）
    return (
      <>
        {/* 步骤指示器 */}
        {viewMode !== 'upload' && (
          <Box sx={{ width: '100%', mb: 4 }}>
            <Stepper alternativeLabel activeStep={activeStep} connector={<ColorlibConnector />}>
              {getSteps().map((label) => (
                <Step key={label}>
                  <StepLabel StepIconComponent={ColorlibStepIcon}>
                    <Typography sx={{ color: '#e2e8f0', fontWeight: 500 }}>{label}</Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        )}

        {/* 步骤内容 */}
        {renderStepContent()}
      </>
    );
  };

  // 获取对话框标题
  const getDialogTitle = () => {
    const unnamed = t('yuyan_unnamed_project');
    switch (viewMode) {
      case 'list':
        return t('yuyan_mgmt');
      case 'create':
        return t('yuyan_create');
      case 'edit':
        const editProject = editingProject?.project || editingProject;
        return t('yuyan_edit', { name: editProject?.project_name || unnamed });
      case 'upload':
        const uploadProject = uploadingProject?.project || uploadingProject;
        return uploadDocumentType === 'proposal'
          ? t('yuyan_upload_proposal', { name: uploadProject?.project_name || unnamed })
          : t('yuyan_upload_conclusion', { name: uploadProject?.project_name || unnamed });
      case 'evaluation':
        const evalProject = viewingEvaluationProject?.project || viewingEvaluationProject;
        return t('yuyan_eval_result', { name: evalProject?.project_name || unnamed });
      default:
        return t('yuyan_mgmt');
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      className="workbench-dialog yuyan-project-dialog"
      PaperProps={{
        className: 'workbench-dialog-paper',
        style: { '--workbench-accent': currentUser?.branding?.themeColor || '#4D8BFF' },
        sx: {
          minHeight: '600px',
          maxHeight: 'calc(100vh - 64px)',
          width: 'min(95vw, 1400px)',
          background: '#1e1e1e',
          color: '#e7e7e7',
          display: 'flex',
          flexDirection: 'column',
          margin: 'auto',
          borderRadius: '10px',
          border: '1px solid #3e3e42',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.38)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle className="workbench-dialog-title" sx={{
        background: '#202124',
        backdropFilter: 'none',
        borderTop: `2px solid ${currentUser?.branding?.themeColor || '#4D8BFF'}`,
        borderBottom: '1px solid #3e3e42',
        color: '#e7e7e7',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 2,
        px: 3
      }}>
        <Box display="flex" alignItems="center">
          {viewMode !== 'list' && (
            <Tooltip title={t('yuyan_tooltip_back')}>
              <IconButton onClick={handleBackToList} sx={{ color: '#94a3b8', mr: 1, '&:hover': { color: '#f8fafc', background: 'rgba(255,255,255,0.1)' } }}>
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
          )}
          <FlightTakeoffIcon sx={{ mr: 1.5, color: currentUser?.branding?.themeColor || '#4D8BFF' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.5px' }}>
            {getDialogTitle()}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {viewMode === 'list' && (
            <>
              <Tooltip title={t('yuyan_tooltip_refresh')}>
                <IconButton onClick={fetchUserProjects} sx={{ color: '#94a3b8', '&:hover': { color: '#38bdf8', background: 'rgba(56, 189, 248, 0.1)' } }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button 
                variant="contained"
                size="medium"
                startIcon={<FlightTakeoffIcon />}
                onClick={() => handleViewModeChange('create', 'proposal')}
                sx={{ 
                  mr: 1,
                  background: currentUser?.branding?.themeColor || '#4D8BFF',
                  boxShadow: 'none',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '6px',
                  '&:hover': {
                    background: currentUser?.branding?.themeColor || '#4D8BFF',
                    boxShadow: 'none',
                  }
                }}
              >
                {t('yuyan_btn_new')}
              </Button>
            </>
          )}
          <IconButton onClick={handleClose} sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444', background: 'rgba(239, 68, 68, 0.1)' } }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* 全局加载进度条 */}
      {isLoading && (
        <Box sx={{ width: '100%', position: 'absolute', top: 64, left: 0, zIndex: 10 }}>
          <LinearProgress sx={{ 
            backgroundColor: '#292a2d',
            '& .MuiLinearProgress-bar': {
              background: currentUser?.branding?.themeColor || '#4D8BFF'
            }
          }} />
        </Box>
      )}

      <DialogContent className="workbench-dialog-content" sx={{
        p: 0, 
        overflow: 'auto',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'rgba(255, 255, 255, 0.2)',
        }
      }}>
        <Box sx={{ 
          p: viewMode === 'evaluation' ? 0 : 4,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* 错误和成功提示 */}
          {(error || success) && (
            <Box sx={{ mb: 3, px: viewMode === 'evaluation' ? 3 : 0 }}>
              {error && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    background: 'rgba(239, 68, 68, 0.1)', 
                    color: '#fca5a5', 
                    border: '1px solid rgba(239, 68, 68, 0.2)',
                    '& .MuiAlert-icon': { color: '#fca5a5' }
                  }}
                >
                  {error}
                </Alert>
              )}
              {success && (
                <Alert 
                  severity="success"
                  sx={{ 
                    background: 'rgba(34, 197, 94, 0.1)', 
                    color: '#86efac', 
                    border: '1px solid rgba(34, 197, 94, 0.2)',
                    '& .MuiAlert-icon': { color: '#86efac' }
                  }}
                >
                  {success}
                </Alert>
              )}
            </Box>
          )}
          
          {/* 加载状态信息 */}
          {isLoading && loadingMessage && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mb: 3,
              p: 2,
              mx: viewMode === 'evaluation' ? 3 : 0,
              background: 'rgba(30, 41, 59, 0.6)',
              borderRadius: '12px',
              border: '1px solid rgba(56, 189, 248, 0.1)'
            }}>
              <CircularProgress size={20} sx={{ mr: 2, color: '#38bdf8' }} />
              <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
                {loadingMessage}
              </Typography>
            </Box>
          )}
          
          {/* 主要内容 */}
          {renderMainContent()}
          
          {/* 表单操作按钮区域 - 仅在表单视图显示 */}
          {viewMode !== 'list' && viewMode !== 'evaluation' && (
            <Box sx={{ 
              mt: 4,
              pt: 3,
              borderTop: '1px solid rgba(255, 255, 255, 0.05)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Button 
                onClick={handleBackToList} 
                sx={{ color: '#94a3b8', '&:hover': { color: '#e2e8f0', background: 'rgba(255,255,255,0.05)' } }}
              >
                {t('yuyan_btn_cancel')}
              </Button>
              <Box display="flex" gap={2}>
                {activeStep > 0 && viewMode !== 'upload' && (
                  <Button 
                    onClick={handleBack}
                    variant="outlined"
                    sx={{ 
                      color: '#e2e8f0', 
                      borderColor: 'rgba(255,255,255,0.2)',
                      '&:hover': { borderColor: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.05)' }
                    }}
                  >
                    {t('yuyan_btn_prev')}
                  </Button>
                )}
                {(viewMode === 'upload' || activeStep === getSteps().length - 1) ? (
                  <Button 
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isLoading}
                    sx={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.5)',
                      minWidth: '120px',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                        boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.5)'
                      },
                      '&.Mui-disabled': {
                        background: 'rgba(59, 130, 246, 0.3)',
                        color: 'rgba(255,255,255,0.3)'
                      }
                    }}
                  >
                    {isLoading ? (
                      loadingMessage ? t('yuyan_btn_processing') : t('yuyan_btn_submitting')
                    ) : 
                      viewMode === 'upload' ? (
                        (uploadingProject?.documents || []).some(doc => doc.document_category === uploadDocumentType) 
                          ? t('yuyan_btn_upload_reupload') 
                          : t('yuyan_btn_upload_start')
                      ) :
                      viewMode === 'edit' ? t('yuyan_btn_save') :
                      t('yuyan_btn_create')}
                  </Button>
                ) : (
                  <Button 
                    onClick={handleNext}
                    variant="contained"
                    disabled={isLoading}
                    sx={{
                      background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                      boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.5)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                        boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.5)'
                      }
                    }}
                  >
                    {t('yuyan_btn_next')}
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default YuyanProjectDialog;